import { useState, useEffect } from "react";
import { api, errMsg, readFilesAsDataURLs } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CheckCircle, ShieldCheck, X, Image as ImageIcon } from "@phosphor-icons/react";
import { AiSuggest } from "@/components/AiSuggest";
import { useCurrencySymbol } from "@/hooks/useCurrencySymbol";

export default function StepPanel({ product, stepMeta, onSaved }) {
  const currencySymbol = useCurrencySymbol();
  const { can } = useAuth();
  const step = product.steps?.[stepMeta.key] || { completed: false, data: {}, notes: "", images: [] };
  const [data, setData] = useState(step.data || {});
  const [notes, setNotes] = useState(step.notes || "");
  const [images, setImages] = useState(step.images || []);
  const [completed, setCompleted] = useState(step.completed || false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const s = product.steps?.[stepMeta.key] || {};
    setData(s.data || {});
    setNotes(s.notes || "");
    setImages(s.images || []);
    setCompleted(s.completed || false);
  }, [stepMeta.key, product]);

  const isApproval = !!stepMeta.approval;
  const canEdit = isApproval ? can("approvals.manage") : can("steps.update") || can("approvals.manage");

  const missing = stepMeta.fields.filter((f) => f.name !== "notes" && !String(data[f.name] ?? "").trim()).map((f) => f.label);
  if (stepMeta.images && images.length === 0) missing.push("Images");
  if (stepMeta.key === "batch_photos" && images.length > 0 && images.length < 4) missing.push("4 batch photos (2 before + 2 after)");

  const handleToggle = (v) => {
    if (v && missing.length) {
      toast.error(`Fill required fields first: ${missing.join(", ")}`);
      return;
    }
    setCompleted(v);
  };

  const addImages = async (e) => {
    const urls = await readFilesAsDataURLs(e.target.files);
    setImages((imgs) => [...imgs, ...urls].slice(0, 12));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${product.id}/steps/${stepMeta.key}`, { data, notes, images, completed });
      toast.success(completed ? `${stepMeta.title} completed` : `${stepMeta.title} saved`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const totalCost = stepMeta.key === "costing"
    ? ["ingredient_cost", "labour_cost", "packaging_cost", "overhead_cost"].reduce((s, k) => s + (Number(data[k]) || 0), 0)
    : null;

  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid={`step-panel-${stepMeta.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900">
              Step {stepMeta.order}: {stepMeta.title}
            </h3>
            {isApproval && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF7ED] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#C2410C]">
                <ShieldCheck size={12} /> Approval
              </span>
            )}
            {stepMeta.key === "product_idea" && canEdit && (
              <AiSuggest
                kind="idea"
                context={{ name: product.name, category: product.category, egg_type: product.egg_type, diet_type: product.diet_type }}
                onApply={(item) => setData({ ...data, concept: item.value })}
                testid="ai-idea-suggest"
              />
            )}
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            {step.updated_at ? `Last updated by ${step.updated_by}` : "Not started yet"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {completed && <CheckCircle size={22} weight="fill" className="text-[#10B981]" data-testid="step-completed-icon" />}
          <Switch checked={completed} onCheckedChange={handleToggle} disabled={!canEdit} data-testid="step-complete-toggle" />
          <span className="text-xs font-semibold text-slate-600">Complete</span>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {!canEdit && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="step-no-permission">
            You don't have permission to edit this step{isApproval ? " (requires approval rights)" : ""}.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {stepMeta.fields.map((f) => (
            <div key={f.name} className={`space-y-1.5 ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
              <Label>{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`step-field-${f.name}`} rows={3} disabled={!canEdit} value={data[f.name] || ""} onChange={(e) => setData({ ...data, [f.name]: e.target.value })} />
              ) : (
                <Input data-testid={`step-field-${f.name}`} type={f.type} disabled={!canEdit} value={data[f.name] || ""} onChange={(e) => setData({ ...data, [f.name]: e.target.value })} />
              )}
            </div>
          ))}
        </div>

        {totalCost !== null && (
          <div className="rounded-md border border-[#FDBA74] bg-[#FFF7ED] px-4 py-2.5 text-sm font-semibold text-[#C2410C]" data-testid="costing-total">
            Total cost: {currencySymbol}{totalCost.toFixed(2)}
          </div>
        )}

        {stepMeta.images && (
          <div className="space-y-1.5">
            <Label>Images {stepMeta.images_hint && <span className="ml-1 text-xs font-normal text-slate-400">({stepMeta.images_hint})</span>}</Label>
            <div className="flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="relative h-20 w-20">
                  <img src={img} alt="" className="h-20 w-20 rounded-md border border-slate-200 object-cover" />
                  {canEdit && (
                    <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full border border-slate-200 bg-white p-0.5 shadow">
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
              {canEdit && (
                <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400 transition-colors hover:border-[#EA580C] hover:text-[#EA580C]" data-testid="step-image-upload">
                  <ImageIcon size={20} />
                  <input type="file" accept="image/*" multiple className="hidden" onChange={addImages} />
                </label>
              )}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea data-testid="step-notes-input" rows={2} disabled={!canEdit} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes for this step" />
        </div>

        {canEdit && !completed && missing.length > 0 && (
          <p className="text-xs font-medium text-amber-700" data-testid="step-missing-hint">
            To complete this step, fill: {missing.join(", ")}
          </p>
        )}

        {canEdit && (
          <Button onClick={save} disabled={saving} data-testid="step-save-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
            {saving ? "Saving…" : "Save step"}
          </Button>
        )}
      </div>
    </div>
  );
}
